/**
 * Turning height and climate into terrain and features.
 *
 * Two rules govern this file:
 *
 *   Relief thresholds are QUANTILES of the land, not absolute heights. Asking for
 *   "7% mountains" and getting 7% on every seed is worth far more than a fixed
 *   cutoff that yields a flat world on one seed and an impassable one on the next.
 *
 *   Nothing here hard-codes which terrain a feature may sit on. That comes from
 *   `validTerrain` in the content files, so adding a feature is a data change. The
 *   moment this file contains `if (terrain === 'grassland')`, the data-driven design
 *   has started leaking back into code.
 */

import type { Content } from '../../../content/schema/index.js';
import { chance, nextFloat, type RngState } from '../rng/index.js';
import type { TileGrid } from '../state/types.js';
import { quantile } from './elevation.js';
import type { ClimateField } from './climate.js';
import type { MapgenParams } from './params.js';

export interface ReliefThresholds {
  hill: number;
  mountain: number;
}

/** Land-only height quantiles for the hill and mountain bands. */
export function reliefThresholds(
  height: Float32Array,
  isLand: Uint8Array,
  params: MapgenParams,
): ReliefThresholds {
  const landHeights: number[] = [];
  for (let i = 0; i < height.length; i++) if (isLand[i] === 1) landHeights.push(height[i]!);
  if (landHeights.length === 0) return { hill: 1, mountain: 1 };

  const sorted = Float32Array.from(landHeights);
  // Mountains are the highest `mountainFraction` of land; hills the band below.
  const mountain = quantile(sorted, 1 - params.mountainFraction);
  const hill = quantile(sorted, 1 - params.mountainFraction - params.hillFraction);
  return { hill, mountain };
}

export function assignTerrain(
  grid: TileGrid,
  content: Content,
  height: Float32Array,
  isLand: Uint8Array,
  isLake: Uint8Array,
  isCoast: Uint8Array,
  climate: ClimateField,
  params: MapgenParams,
): ReliefThresholds {
  const T = {
    ocean: content.terrain.id('ocean'),
    coast: content.terrain.id('coast'),
    lake: content.terrain.id('lake'),
    grassland: content.terrain.id('grassland'),
    plains: content.terrain.id('plains'),
    desert: content.terrain.id('desert'),
    tundra: content.terrain.id('tundra'),
    hills: content.terrain.id('hills'),
    mountains: content.terrain.id('mountains'),
  };

  const relief = reliefThresholds(height, isLand, params);

  for (let i = 0; i < grid.terrain.length; i++) {
    if (isLand[i] === 0) {
      grid.terrain[i] = isLake[i] === 1 ? T.lake : isCoast[i] === 1 ? T.coast : T.ocean;
      // Water elevation is stored but flattened; nothing reads relief under the sea,
      // and a varying seabed would only confuse the renderer's shading later.
      grid.elevation[i] = Math.round(Math.min(255, height[i]! * 60));
      continue;
    }

    grid.elevation[i] = Math.round(Math.min(255, Math.max(0, height[i]! * 255)));

    if (height[i]! >= relief.mountain) {
      grid.terrain[i] = T.mountains;
      continue;
    }
    if (height[i]! >= relief.hill) {
      grid.terrain[i] = T.hills;
      continue;
    }

    const temperature = climate.temperature[i]!;
    const moisture = climate.moisture[i]!;

    if (temperature < params.tundraTemperature) {
      grid.terrain[i] = T.tundra;
    } else if (moisture < params.desertMoisture && temperature >= params.desertTemperature) {
      // Deserts need heat as well as drought. Cold dry land is steppe, not sand —
      // without this check the whole temperate band turns to desert wherever the
      // prevailing wind has already dropped its rain.
      grid.terrain[i] = T.desert;
    } else if (moisture >= params.grasslandMoisture) {
      grid.terrain[i] = T.grassland;
    } else {
      grid.terrain[i] = T.plains;
    }
  }

  return relief;
}

/**
 * Place features on terrain their content entry permits.
 *
 * Each feature has a climate window; the density parameters widen or narrow it
 * rather than acting as a blind dice roll, so raising `forestDensity` grows forest
 * into the climates next to where it already is, instead of speckling it everywhere.
 */
export function assignFeatures(
  grid: TileGrid,
  content: Content,
  isLand: Uint8Array,
  climate: ClimateField,
  params: MapgenParams,
  rng: RngState,
): void {
  const forest = content.features.byKey.get('forest');
  const rainforest = content.features.byKey.get('rainforest');
  const marsh = content.features.byKey.get('marsh');
  const oasis = content.features.byKey.get('oasis');

  /** A feature may only be placed where its own content entry allows. */
  const permitted = (feature: { validTerrain: string[] } | undefined, terrainId: number): boolean =>
    feature !== undefined && feature.validTerrain.includes(content.terrain.get(terrainId).key);

  for (let i = 0; i < grid.terrain.length; i++) {
    if (isLand[i] === 0) continue;

    const terrainId = grid.terrain[i]!;
    const temperature = climate.temperature[i]!;
    const moisture = climate.moisture[i]!;
    const roll = nextFloat(rng);

    // Rainforest first: it occupies the hot, saturated corner that would otherwise
    // qualify as forest, and checking it second would leave it almost never placed.
    if (
      permitted(rainforest, terrainId) &&
      temperature >= params.rainforestTemperature &&
      moisture >= params.grasslandMoisture + 0.15 &&
      roll < params.rainforestDensity * 3
    ) {
      grid.feature[i] = rainforest!.id;
      continue;
    }

    if (permitted(forest, terrainId)) {
      // Forest likes moisture and dislikes extreme heat and cold. The window widens
      // with forestDensity rather than the roll simply passing more often.
      const climateWindow = params.forestDensity;
      const suitability =
        Math.min(1, moisture / Math.max(1e-6, params.grasslandMoisture)) *
        (1 - Math.abs(temperature - 0.55) * 1.4);
      if (suitability > 0 && roll < suitability * climateWindow * 2.2) {
        grid.feature[i] = forest!.id;
        continue;
      }
    }

    if (
      permitted(marsh, terrainId) &&
      moisture >= params.grasslandMoisture + 0.2 &&
      roll < params.marshDensity * 2
    ) {
      grid.feature[i] = marsh!.id;
      continue;
    }

    if (permitted(oasis, terrainId) && chance(rng, params.oasisChance)) {
      grid.feature[i] = oasis!.id;
    }
  }
}
